import { Card, Divider, Flex, Tabs } from 'antd';
import CustomLoader from 'components/CustomLoader';
import NoData from 'components/NoData';
import TagComponent from 'components/Tags';
import TaskInfo from 'components/TaskInfo';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFertilizerQuery from 'screens/awd-dashboard/hooks/queries/useFertilizerQuery';
import { SelectTaskData } from 'screens/sales-group-progress';
import { getStatusValues, renderDate } from 'utils/helpers';

const FertilizerLogContent = ({ data }: { data: SelectTaskData }) => {
  const { task } = { ...data };
  const { status, inputDate } = { ...task };
  const [selectedTab, setSelectedTab] = useState<string>('1');

  const { t } = useTranslation();

  const { data: fertilizerData, isLoading } = useFertilizerQuery(task?.id);

  // Filter fertilizers based on tab
  const fertilizerTabsData = useMemo(
    () => ({
      '1': {
        title: t('fertilizerHistory.basalTab'),
        data: fertilizerData?.data?.filter(
          (fertilizer) => fertilizer?.applicationType === 'BASAL',
        ),
      },
      '2': {
        title: t('fertilizerHistory.topDressing1Tab'),
        data: fertilizerData?.data?.filter(
          (fertilizer) => fertilizer?.applicationType === 'TOP_DRESSING_1',
        ),
      },
      '3': {
        title: t('fertilizerHistory.topDressing2Tab'),
        data: fertilizerData?.data?.filter(
          (fertilizer) => fertilizer?.applicationType === 'TOP_DRESSING_2',
        ),
      },
    }),
    [fertilizerData],
  );

  const selectedFertilizer =
    fertilizerTabsData?.[selectedTab as keyof typeof fertilizerTabsData];

  const tabItems = Object.keys(fertilizerTabsData).map((key) => ({
    key,
    label: fertilizerTabsData[key as keyof typeof fertilizerTabsData]?.title,
    children: selectedFertilizer?.data?.map((fertilizerData, index) => (
      <Card
        key={fertilizerData?.id + index}
        className="info-card"
        title={`${selectedFertilizer?.title} - ${index + 1}`}
        bordered
      >
        <div className="info-container">
          <TaskInfo header={t('fertilizerHistory.fertiliserType')}>
            {fertilizerData?.fertilizerType}
          </TaskInfo>
          <TaskInfo header={t('fertilizerHistory.fertiliserName')}>
            {fertilizerData?.name}
          </TaskInfo>
          <TaskInfo header={t('fertilizerHistory.dosage')}>
            {fertilizerData?.dosage}
          </TaskInfo>
          <TaskInfo header={t('fertilizerHistory.advisedDate')}>
            {renderDate(fertilizerData?.applicationDate)}
          </TaskInfo>
          <Divider />
          <Flex
            justify="space-between"
            align="center"
            style={{ width: '100%' }}
          >
            <TaskInfo header="Nitrogen">
              {fertilizerData?.nitrogenComposition}
            </TaskInfo>
            <TaskInfo header="Phosphorus">
              {fertilizerData?.phosphorusComposition}
            </TaskInfo>
            <TaskInfo header="Potassium">
              {fertilizerData?.potassiumComposition}
            </TaskInfo>
            <TaskInfo header="Sulfur">
              {fertilizerData?.sulfurComposition}
            </TaskInfo>
          </Flex>
        </div>
      </Card>
    )),
  }));

  const { text: iconText, color: iconColor } = getStatusValues(status);

  return (
    <>
      {isLoading ? (
        <div className="loader-container">
          <CustomLoader size="extra-large" />
        </div>
      ) : (
        <>
          <Card>
            <div className="info-container">
              <TaskInfo header={t('fertilizerHistory.fertiliserStatus')}>
                <TagComponent color={iconColor} label={iconText} />
              </TaskInfo>
              <TaskInfo header={t('dashboard.taskCompletionDate')}>
                {renderDate(inputDate)}
              </TaskInfo>
            </div>
          </Card>
          <Tabs
            animated
            items={tabItems}
            defaultActiveKey={selectedTab?.toString()}
            onChange={setSelectedTab}
          />
          {selectedFertilizer?.data?.length === 0 && (
            <NoData message={t('fertilizerHistory.noFertiliserData')} />
          )}
        </>
      )}
    </>
  );
};

export default FertilizerLogContent;
